import React, { useEffect, useState, useRef } from "react";
import { getShopCustomersAndFollowers, toAbsUrl, sendMessageToUser } from "../utils/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../styles/shopCustomers.css";

export default function ShopCustomers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightUserId = searchParams.get("userId") || "";
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [error, setError] = useState("");
  const highlightRef = useRef(null);
  const [activeTab, setActiveTab] = useState("customers"); // "customers" | "followers"
  
  // Bulk messaging state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkMessageText, setBulkMessageText] = useState("");
  const [bulkTarget, setBulkTarget] = useState(""); // "customers" | "followers"
  const [sendingBulk, setSendingBulk] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-scroll to highlighted user when data loads
  // If buyer is not a customer or follower → redirect to messages
  useEffect(() => {
    if (!highlightUserId || loading) return;
    // Check if user is in customers or followers and switch tab
    const inCustomers = customers.some(c => String(c._id || c.userId || "") === highlightUserId);
    const inFollowers = !inCustomers && followers.some(f => String(f._id || f.userId || "") === highlightUserId);

    if (!inCustomers && !inFollowers) {
      // Buyer is not a customer or follower — open messaging instead
      navigate("/shop/messages", {
        replace: true,
        state: { startNewConvo: true, participantType: "user", participantId: highlightUserId },
      });
      return;
    }

    if (inFollowers) setActiveTab("followers");
    else if (inCustomers) setActiveTab("customers");

    // Scroll to highlighted card after render
    setTimeout(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  }, [highlightUserId, loading, customers, followers, navigate]);

  async function loadData() {
    try {
      setLoading(true);
      setError("");
      
      const data = await getShopCustomersAndFollowers();
      setCustomers(data?.customers || []);
      setFollowers(data?.followers || []);
    } catch (err) {
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  // Message individual user
  function messageUser(userId) {
    navigate('/shop/messages', {
      state: {
        startNewConvo: true,
        participantType: 'user',
        participantId: userId,
      },
    });
  }

  // Open bulk message modal
  function openBulkMessage(target) {
    setBulkTarget(target);
    setBulkMessageText("");
    setBulkResult(null);
    setShowBulkModal(true);
  }

  // Send bulk messages
  async function sendBulkMessages() {
    if (!bulkMessageText.trim()) {
      alert("Please enter a message");
      return;
    }

    const targetList = bulkTarget === "customers" ? customers : followers;
    if (targetList.length === 0) {
      alert(`No ${bulkTarget} to message`);
      return;
    }


    setSendingBulk(true);
    setBulkProgress({ current: 0, total: targetList.length });
    
    let sent = 0;
    let failed = 0;
    const failedUsers = [];

    for (let i = 0; i < targetList.length; i++) {
      const user = targetList[i];
      setBulkProgress({ current: i + 1, total: targetList.length });

      try {
        // Use simplified endpoint that handles conversation creation
        await sendMessageToUser(user.userId, bulkMessageText.trim());
        sent++;
        // Stay under the 40-messages-a-minute limit.
        if (i < targetList.length - 1) await new Promise((r) => setTimeout(r, 1600));
      } catch (err) {
        console.error(`Failed to message user ${user.userId}:`, err);
        failed++;
        failedUsers.push(user.name || user.email || 'Unknown');
      }

      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setSendingBulk(false);
    setBulkResult({ 
      sent, 
      failed, 
      total: targetList.length,
      failedUsers: failedUsers.slice(0, 10) // Only show first 10 failed
    });
  }

  if (loading) {
    return (
      <div className="page-responsive flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-slate-600">Loading customers...</div>
      </div>
    );
  }

  const activeList = activeTab === "customers" ? customers : followers;

  return (
    <div className="page-responsive shop-customers bg-slate-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Customers & Followers</h1>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Total Customers</div>
          <div className="text-2xl font-bold text-slate-900">{customers.length}</div>
          <div className="text-xs text-slate-500 mt-1">Made purchases</div>
        </div>
        
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Followers</div>
          <div className="text-2xl font-bold text-slate-900">{followers.length}</div>
          <div className="text-xs text-slate-500 mt-1">Haven't purchased yet</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
          <div className="text-xs uppercase font-semibold text-slate-500 mb-1">Total Users</div>
          <div className="text-2xl font-bold text-slate-900">{customers.length + followers.length}</div>
          <div className="text-xs text-slate-500 mt-1">All engaged users</div>
        </div>
      </div>

      {/* Bulk Message Buttons */}
      <div className="flex gap-3 mb-4">
        <button
          onClick={() => openBulkMessage("customers")}
          disabled={customers.length === 0}
          className="sc-bulk-btn sc-bulk-customers"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          Message All Customers ({customers.length})
        </button>
        
        <button
          onClick={() => openBulkMessage("followers")}
          disabled
          title="To protect people's inboxes, shops can message customers who bought from them, people who opted in to shop messages, or people who wrote to the shop first."
          className="sc-bulk-btn sc-bulk-followers"
          style={{ opacity: 0.55, cursor: "not-allowed" }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
          Message All Followers ({followers.length})
        </button>
        <div className="text-xs text-slate-500" style={{ width: "100%" }}>
          Followers can be messaged once they buy from you or write to you first.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-200">
        <button
          onClick={() => setActiveTab("customers")}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === "customers"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Customers ({customers.length})
        </button>
        <button
          onClick={() => setActiveTab("followers")}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === "followers"
              ? "text-blue-600 border-b-2 border-blue-600"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          Followers ({followers.length})
        </button>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        {activeList.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            {activeTab === "customers" 
              ? "No customers yet. Start selling to see customers here!"
              : "No followers yet. Share your shop to get followers!"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">User</th>
                  {activeTab === "customers" && (
                    <>
                      <th className="text-right p-4 font-semibold text-slate-700">Orders</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Total Spent</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Last Order</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                    </>
                  )}
                  {activeTab === "followers" && (
                    <>
                      <th className="text-right p-4 font-semibold text-slate-700">Followed On</th>
                      <th className="text-right p-4 font-semibold text-slate-700">Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {activeList.map((user, idx) => {
                  const uid = String(user._id || user.userId || "");
                  const isHighlighted = highlightUserId && uid === highlightUserId;
                  return (
                  <tr
                    key={user.userId || idx}
                    ref={isHighlighted ? highlightRef : null}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${isHighlighted ? "sc-highlighted" : ""}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                          {user.avatarUrl ? (
                            <img
                              src={toAbsUrl(user.avatarUrl)}
                              alt={user.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-600 font-semibold">
                              {(user.name || "U").charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{user.name || "User"}</div>
                          {user.email ? <div className="text-xs text-slate-500">{user.email}</div> : null}
                        </div>
                      </div>
                    </td>
                    
                    {activeTab === "customers" && (
                      <>
                        <td className="p-4 text-right font-medium text-slate-900">
                          {user.orderCount || 0}
                        </td>
                        <td className="p-4 text-right font-medium text-green-600">
                          {formatCurrency(user.totalSpent, user.currency)}
                        </td>
                        <td className="p-4 text-right text-slate-600">
                          {formatDate(user.lastOrderDate)}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => messageUser(user.userId)}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold transition-colors inline-flex items-center gap-1.5 shadow-sm"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            Message
                          </button>
                        </td>
                      </>
                    )}
                    
                    {activeTab === "followers" && (
                      <>
                        <td className="p-4 text-right text-slate-600">
                          {formatDate(user.followedAt)}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => messageUser(user.userId)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold transition-colors inline-flex items-center gap-1.5 shadow-sm"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            Message
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                  ); })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk Message Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-slate-200 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900">
                  Message All {bulkTarget === "customers" ? "Customers" : "Followers"}
                </h2>
                <button
                  onClick={() => setShowBulkModal(false)}
                  disabled={sendingBulk}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-slate-600 mt-2">
                Send a message to all {bulkTarget === "customers" ? customers.length : followers.length} {bulkTarget}
              </p>
            </div>

            <div className="p-6">
              {!bulkResult ? (
                <>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Message
                  </label>
                  <textarea
                    value={bulkMessageText}
                    onChange={(e) => setBulkMessageText(e.target.value)}
                    disabled={sendingBulk}
                    placeholder="Type your message here..."
                    className="w-full bg-white border border-slate-300 rounded-lg p-3 text-slate-900 placeholder-slate-400 min-h-[150px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:bg-slate-50 disabled:opacity-50"
                  />

                  {sendingBulk && (
                    <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm font-medium text-slate-900">
                          Sending messages... {bulkProgress.current} / {bulkProgress.total}
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-blue-600 h-full transition-all duration-300"
                          style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowBulkModal(false)}
                      disabled={sendingBulk}
                      className="flex-1 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendBulkMessages}
                      disabled={sendingBulk || !bulkMessageText.trim()}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors shadow-sm"
                    >
                      {sendingBulk ? "Sending..." : "Send Messages"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-center py-6">
                    <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${bulkResult.failed === 0 ? "bg-green-50" : "bg-amber-50"}`}>
                      <svg className={`w-8 h-8 ${bulkResult.failed === 0 ? "text-green-600" : "text-amber-600"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {bulkResult.failed === 0 ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        )}
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                      {bulkResult.failed === 0 ? "All Messages Sent!" : "Messages Sent"}
                    </h3>
                    <div className="space-y-1 text-slate-600">
                      <p className="text-green-600 font-medium">✓ {bulkResult.sent} messages sent successfully</p>
                      {bulkResult.failed > 0 && (
                        <p className="text-amber-600 font-medium">⚠ {bulkResult.failed} messages failed</p>
                      )}
                      <p className="text-sm mt-2">Total: {bulkResult.total} {bulkTarget}</p>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowBulkModal(false);
                      setBulkResult(null);
                      setBulkMessageText("");
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-colors shadow-sm"
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
